import React from 'react';
import { Box } from '@mui/material';
import type { JobResponse } from '@taxassist/api-client';
import { KanbanColumn } from './KanbanColumn';
import { groupJobsByUserFriendlyStatus } from '../../utils/kanbanUtils';
import { type UserFriendlyStatus } from '../../utils/statusMapping';

const COLUMN_ORDER: UserFriendlyStatus[] = [
  'Unsubmitted',
  'Queued',
  'In Progress',
  'Download to Process',
  'Done',
];

interface KanbanBoardProps {
  jobs: JobResponse[];
  isLoading: boolean;
  onCreateJob: () => void;
  onUploadFiles?: (job: JobResponse) => void;
  onViewDetails?: (job: JobResponse) => void;
}

export const KanbanBoard = React.memo(({ jobs, isLoading, onCreateJob, onUploadFiles, onViewDetails }: KanbanBoardProps) => {
  // Group jobs by their user-friendly status for display
  const groupedJobs = React.useMemo(() => groupJobsByUserFriendlyStatus(jobs), [jobs]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)', // 5 equal columns for our 5 statuses
        gap: 2,
        alignItems: 'start', // Align columns to top instead of stretching
        // Remove fixed minHeight to let content determine height
        '@media (max-width: 1200px)': {
          // On smaller screens, allow horizontal scroll
          display: 'flex',
          overflowX: 'auto',
          alignItems: 'stretch', // Keep columns same height on mobile
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 4,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        },
      }}
    >
      {COLUMN_ORDER.map((userFriendlyStatus) => (
        <KanbanColumn
          key={userFriendlyStatus}
          status={userFriendlyStatus}
          jobs={groupedJobs[userFriendlyStatus] || []}
          isLoading={isLoading}
          onCreateJob={userFriendlyStatus === 'Unsubmitted' ? onCreateJob : undefined}
          onUploadFiles={onUploadFiles}
          onViewDetails={onViewDetails}
        />
      ))}
    </Box>
  );
});
